import { useMemo } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { orderBy } from 'lodash'

import { getListedChainIds, SupportedVmTypes } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { IAddress, VmType } from '~/types'
import { Address } from '~/utils/address'
import { captureException } from '~/utils/errors'
import { retryExponentialBackoff } from '~/utils/requests'

export type RelayFeaturedToken = {
  address: IAddress
  symbol: string
  name: string
  decimals: number
  supportsBridging: boolean
  metadata: {
    logoURI: string
  }
  id: string
}

export type RelayNetworkMetadata = {
  id: number
  iconUrl: string
  displayName: string
  vmType: string
  disabled: boolean
  depositEnabled: boolean
  featuredTokens: RelayFeaturedToken[]
  currency: {
    id: string
    symbol: string
    name: string
    address: string
    decimals: number
    supportsBridging: boolean
  }
}

export type RelayCurrencySearchResult = {
  chainId: number
  address: IAddress
  symbol: string
  name: string
  decimals: number
  vmType: string
  metadata: {
    logoURI: string
    verified: boolean
    isNative: boolean
  }
}

/**
 * Maps Relay currency search results to RelayFeaturedToken format
 */
export const mapRelayCurrencyToFeaturedToken = (
  currency: RelayCurrencySearchResult,
): RelayFeaturedToken => ({
  address: currency.address,
  symbol: currency.symbol,
  name: currency.name,
  decimals: currency.decimals,
  supportsBridging: false, // Search results don't include this info
  metadata: {
    logoURI: currency.metadata.logoURI,
  },
  id: `${currency.chainId}-${Address.format(currency.address)}`,
})

/**
 * Hook to fetch networks supported by Relay bridge. Returns every bridgeable
 * network, including the org's active chains — consumers listing "networks to
 * add" filter those out themselves.
 */
export const useRelayNetworks = ({
  enabled,
  vmTypes,
}: {
  enabled: boolean
  vmTypes?: VmType[]
}) => {
  const queryClient = useQueryClient()

  const allowedVmTypes: string[] = useMemo(() => {
    return vmTypes ?? ['evm']
  }, [vmTypes])

  return useQuery({
    queryKey: [QUERY_KEYS.RELAY_NETWORKS, allowedVmTypes],
    queryFn: async () => {
      const response = await retryExponentialBackoff(
        fetch,
        ['https://api.relay.link/chains'],
        {
          maxTry: 3,
        },
      )

      if (!response.ok) {
        captureException(new Error('Failed to fetch relay networks'))
        return []
      }
      const data = (await response.json()) as { chains: RelayNetworkMetadata[] }

      const filtered = data.chains.filter(
        (chain) =>
          allowedVmTypes.includes(chain.vmType) &&
          !chain.disabled &&
          chain.depositEnabled,
      )

      const sorted = orderBy(
        filtered,
        [(network) => network.displayName],
        ['asc'],
      )

      sorted.forEach((network) => {
        queryClient.setQueryData(
          [QUERY_KEYS.RELAY_NETWORK, network.id],
          network,
        )

        // Cache featured tokens individually for each network. Listed chains
        // are excluded: their token metadata comes from on-chain multicall,
        // and seeding here would overwrite it with Relay's coarser data.
        if (getListedChainIds().includes(network.id)) return
        network.featuredTokens.forEach((token) => {
          const metadata = {
            address: token.address,
            symbol: token.symbol,
            decimals: token.decimals,
            validToken: true,
            logoUri: token.metadata.logoURI,
            isPosition: false,
            chainId: network.id,
          }
          queryClient.setQueryData(
            [
              QUERY_KEYS.TOKEN_METADATA,
              network.id,
              Address.format(token.address),
            ],
            metadata,
          )
        })
      })

      return sorted
    },
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}

/**
 * Hook to get relay network info for a specific chain
 */
export const useRelayNetworkInfo = (chainId: number) => {
  const { data: relayNetworks } = useRelayNetworks({
    enabled: true,
    vmTypes: SupportedVmTypes,
  })

  return relayNetworks?.find((network) => network.id === chainId)
}
